import { cn } from '@/lib/utils'
import { Skeleton } from './skeleton'

export function LoadingBlock({
 label,
 className,
}: {
 label: string
 className?: string
}) {
 return (
  <div role="status" aria-busy="true">
   <Skeleton className={cn('h-16', className)} />
   <span className="sr-only">{label}</span>
  </div>
 )
}

interface TableSkeletonProps {
 rows?: number
 columns?: number
 className?: string
}

export function TableSkeleton({ rows = 5, columns = 4, className }: TableSkeletonProps) {
 return (
  <div className={cn('border border-border rounded-lg overflow-hidden', className)}>
   <div className="flex border-b border-border-subtle bg-muted/30 px-4 py-3 gap-8">
    {Array.from({ length: columns }).map((_, i) => (
     <Skeleton key={i} className="h-3 w-20" />
    ))}
   </div>
   {Array.from({ length: rows }).map((_, i) => (
    <div
     key={i}
     className={cn(
      'flex items-center px-4 py-3 gap-8',
      i !== rows - 1 && 'border-b border-border-subtle'
     )}
    >
     {Array.from({ length: columns }).map((_, columnIndex) => (
      <Skeleton key={columnIndex} className="h-4 w-24" />
     ))}
    </div>
   ))}
  </div>
 )
}

interface MetricsSkeletonProps {
 count?: number
 className?: string
}

export function MetricsSkeleton({ count = 4, className }: MetricsSkeletonProps) {
 return (
  <div className={cn('flex gap-4', className)}>
   {Array.from({ length: count }).map((_, i) => (
    <div key={i} className="flex-1 p-4 border border-border rounded-lg">
     <Skeleton className="h-3 w-16 mb-2" />
     <Skeleton className="h-7 w-12 mb-1" />
     <Skeleton className="h-3 w-24" />
    </div>
   ))}
  </div>
 )
}

interface ListSkeletonProps {
 rows?: number
 showIcon?: boolean
 showAction?: boolean
 className?: string
}

export function ListSkeleton({
 rows = 5,
 showIcon = true,
 showAction = true,
 className,
}: ListSkeletonProps) {
 return (
  <div className={cn('space-y-0', className)}>
   {Array.from({ length: rows }).map((_, i) => (
    <div
     key={i}
     className={cn(
      'flex items-center justify-between py-3 px-4',
      i !== rows - 1 && 'border-b border-border-subtle'
     )}
    >
     <div className="flex items-center gap-3">
      {showIcon && <Skeleton className="h-8 w-8 rounded" />}
      <div className="space-y-1.5">
       <Skeleton className="h-4 w-32" />
       <Skeleton className="h-3 w-48" />
      </div>
     </div>
     {showAction && <Skeleton className="h-8 w-16 rounded" />}
    </div>
   ))}
  </div>
 )
}

interface CardSkeletonProps {
 showHeader?: boolean
 showFooter?: boolean
 lines?: number
 className?: string
}

export function CardSkeleton({
 showHeader = true,
 showFooter = false,
 lines = 3,
 className,
}: CardSkeletonProps) {
 return (
  <div className={cn('border border-border rounded-lg overflow-hidden', className)}>
   {showHeader && (
    <div className="px-5 py-3 border-b border-border-subtle">
     <Skeleton className="h-4 w-32" />
    </div>
   )}
   <div className="p-5 space-y-3">
    {Array.from({ length: lines }).map((_, i) => (
     <Skeleton
      key={i}
      className={cn('h-4', i === lines - 1 ? 'w-2/3' : 'w-full')}
     />
    ))}
   </div>
   {showFooter && (
    <div className="px-5 py-3 border-t border-border-subtle flex justify-end gap-2">
     <Skeleton className="h-8 w-20 rounded" />
     <Skeleton className="h-8 w-20 rounded" />
    </div>
   )}
  </div>
 )
}

interface ActionItemsSkeletonProps {
 count?: number
 className?: string
}

export function ActionItemsSkeleton({ count = 3, className }: ActionItemsSkeletonProps) {
 return (
  <div className={cn('space-y-2', className)}>
   {Array.from({ length: count }).map((_, i) => (
    <div
     key={i}
     className="flex items-center justify-between py-3 px-4 border-l-3 border-l-muted rounded-r-lg bg-muted/20"
    >
     <div className="space-y-1.5">
      <Skeleton className="h-4 w-40" />
      <Skeleton className="h-3 w-56" />
     </div>
     <Skeleton className="h-8 w-20 rounded" />
    </div>
   ))}
  </div>
 )
}

interface ActivityFeedSkeletonProps {
 count?: number
 className?: string
}

export function ActivityFeedSkeleton({ count = 5, className }: ActivityFeedSkeletonProps) {
 return (
  <div className={cn('space-y-0', className)}>
   {Array.from({ length: count }).map((_, i) => (
    <div
     key={i}
     className={cn(
      'py-3 px-4',
      i !== count - 1 && 'border-b border-border-subtle'
     )}
    >
     <div className="flex items-start justify-between">
      <div className="space-y-1.5">
       <Skeleton className="h-4 w-48" />
       <Skeleton className="h-3 w-64" />
      </div>
      <Skeleton className="h-3 w-16" />
     </div>
    </div>
   ))}
  </div>
 )
}

export function PageHeaderSkeleton({ className }: { className?: string }) {
 return (
  <div className={cn('flex items-center justify-between mb-6', className)}>
   <div className="space-y-1">
    <Skeleton className="h-7 w-48" />
    <Skeleton className="h-4 w-64" />
   </div>
   <div className="flex gap-2">
    <Skeleton className="h-9 w-24 rounded" />
    <Skeleton className="h-9 w-32 rounded" />
   </div>
  </div>
 )
}

interface ListingTableSkeletonProps {
 rows?: number
 className?: string
}

export function ListingTableSkeleton({ rows = 5 }: ListingTableSkeletonProps) {
 return (
  <>
   {Array.from({ length: rows }).map((_, i) => (
    <tr key={i}>
     <td className="px-3 py-2.5 pl-4 lg:pl-8">
      <div className="flex items-center gap-3">
       <Skeleton className="h-12 w-12" />
       <div className="space-y-1">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-16" />
       </div>
      </div>
     </td>
     <td className="px-3 py-2.5 hidden sm:table-cell">
      <Skeleton className="h-4 w-24" />
     </td>
     <td className="px-3 py-2.5 hidden md:table-cell">
      <Skeleton className="h-4 w-12" />
     </td>
     <td className="px-3 py-2.5">
      <Skeleton className="h-4 w-16" />
     </td>
     <td className="px-3 py-2.5 hidden lg:table-cell">
      <Skeleton className="h-4 w-16" />
     </td>
     <td className="px-3 py-2.5 pr-4 lg:pr-8">
      <Skeleton className="h-6 w-14" />
     </td>
    </tr>
   ))}
  </>
 )
}

interface ListingCardSkeletonProps {
 count?: number
 className?: string
}

export function ListingCardSkeleton({ count = 5, className }: ListingCardSkeletonProps) {
 return (
  <div className={cn('space-y-3', className)}>
   {Array.from({ length: count }).map((_, i) => (
    <div key={i} className="border border-border bg-background p-3">
     <div className="flex gap-3">
      <Skeleton className="h-20 w-20 shrink-0" />
      <div className="flex-1 space-y-2">
       <Skeleton className="h-4 w-3/4" />
       <Skeleton className="h-3 w-20" />
       <Skeleton className="h-3 w-32 mt-auto" />
      </div>
     </div>
     <div className="flex justify-between mt-3 pt-3 border-t border-border">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-3 w-16" />
     </div>
    </div>
   ))}
  </div>
 )
}

interface ProductsTableSkeletonProps {
 rows?: number
 selectable?: boolean
 className?: string
}

export function ProductsTableSkeleton({ rows = 5, selectable, className }: ProductsTableSkeletonProps) {
 return (
  <div className={cn('border border-border rounded-sm overflow-hidden', className)}>
   <div className="bg-muted border-b border-border px-4 py-3">
    <div className="flex items-center gap-4">
     {selectable && <Skeleton className="size-4 rounded" />}
     <Skeleton className="h-3 w-20" />
     <Skeleton className="h-3 w-16" />
     <Skeleton className="h-3 w-12 ml-auto" />
     <Skeleton className="h-3 w-12" />
    </div>
   </div>
   <div className="divide-y divide-border/50">
    {Array.from({ length: rows }).map((_, i) => (
     <div key={i} className="px-4 py-4 flex items-center gap-4">
      {selectable && <Skeleton className="size-4 rounded" />}
      <Skeleton className="size-10 rounded-md shrink-0" />
      <div className="flex-1 space-y-1.5">
       <Skeleton className="h-4 w-32" />
       <Skeleton className="h-3 w-20" />
      </div>
      <Skeleton className="h-4 w-28" />
      <Skeleton className="h-4 w-12" />
      <Skeleton className="h-4 w-16" />
      <Skeleton className="size-6 rounded" />
     </div>
    ))}
   </div>
  </div>
 )
}
